/**
 * src/pages/ResetPassword.js
 * Magic link landing page for password reset.
 * Reads the token from the URL, shows a new password form,
 * and calls the backend to reset the password on submit.
 */

import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../api/client';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState(''); // '' | loading | success | error
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState(''); // token_expired | token_used | invalid_token

  function validateForm() {
    if (password.length < 12) {
      return 'Password must be at least 12 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one digit';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setErrorCode('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      await api.post('/portal/auth/reset-password', { token, password });
      setStatus('success');
    } catch (err) {
      const data = err.response?.data || {};
      setErrorCode(data.error || '');
      setError(data.message || 'Could not reset password. Please try again.');
      setStatus('error');
    }
  }

  // No token in URL
  if (!token) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--content-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <PageLogo />
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center' }}>
              <ErrorIcon color="#dc2626" bg="#fee2e2" />
              <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                Invalid reset link
              </h1>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                This password reset link is invalid. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="btn btn-primary w-full"
                style={{ justifyContent: 'center', padding: '10px', textDecoration: 'none', display: 'block', textAlign: 'center' }}
              >
                Request a new link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--content-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <PageLogo />
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>

            {/* Success */}
            {status === 'success' && (
              <>
                <div style={{
                  width: '48px', height: '48px',
                  background: '#d1fae5',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  <span style={{ color: '#059669' }}>&#10003;</span>
                </div>
                <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  Password reset!
                </h1>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                  Your password has been changed. You can now sign in with your new password.
                </p>
                <Link
                  to="/login"
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center', padding: '10px', textDecoration: 'none', display: 'block', textAlign: 'center' }}
                >
                  Sign in
                </Link>
              </>
            )}

            {/* Token error (expired / used / invalid) */}
            {status === 'error' && (errorCode === 'token_expired' || errorCode === 'token_used' || errorCode === 'invalid_token') && (
              <>
                <ErrorIcon
                  color={errorCode === 'token_expired' ? '#d97706' : '#dc2626'}
                  bg={errorCode === 'token_expired' ? '#fef3c7' : '#fee2e2'}
                />
                <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  {errorCode === 'token_expired' ? 'Link expired' :
                   errorCode === 'token_used' ? 'Link already used' :
                   'Invalid link'}
                </h1>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                  {error}
                </p>
                <Link
                  to="/forgot-password"
                  className="btn btn-primary w-full"
                  style={{ justifyContent: 'center', padding: '10px', textDecoration: 'none', display: 'block', textAlign: 'center', marginBottom: '12px' }}
                >
                  Request a new link
                </Link>
                <Link
                  to="/login"
                  style={{ fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}
                >
                  Back to sign in
                </Link>
              </>
            )}

            {/* Form state (default + validation errors) */}
            {status !== 'success' && !(status === 'error' && (errorCode === 'token_expired' || errorCode === 'token_used' || errorCode === 'invalid_token')) && (
              <>
                <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px', textAlign: 'left' }}>
                  Choose a new password
                </h1>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px', textAlign: 'left' }}>
                  Enter your new password below
                </p>

                {status === 'error' && error && (
                  <div className="alert alert-error" style={{ marginBottom: '20px', textAlign: 'left' }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-group" style={{ textAlign: 'left' }}>
                    <label className="form-label">New password</label>
                    <input
                      className="form-input"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Min. 12 chars, upper + lower + digit"
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '24px', textAlign: 'left' }}>
                    <label className="form-label">Confirm new password</label>
                    <input
                      className="form-input"
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your new password"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={status === 'loading'}
                    style={{ justifyContent: 'center', padding: '10px' }}
                  >
                    {status === 'loading' ? 'Resetting…' : 'Reset password'}
                  </button>
                </form>

                <p style={{
                  textAlign: 'center',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  marginTop: '20px',
                  marginBottom: 0
                }}>
                  <Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                    Back to sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Shared logo block */
function PageLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
      <div style={{
        width: '36px', height: '36px',
        background: 'var(--accent)',
        borderRadius: '8px'
      }} />
      <div>
        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Process Tracker
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          Client Portal
        </div>
      </div>
    </div>
  );
}

/** Shared error icon */
function ErrorIcon({ color, bg }) {
  return (
    <div style={{
      width: '48px', height: '48px',
      background: bg,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      fontSize: '24px'
    }}>
      <span style={{ color }}>!</span>
    </div>
  );
}
